/**
 * Client-side Category A field classification constants.
 *
 * Source of truth: docs/living/data-classification.md
 *
 * PURPOSE — fail-closed safety net:
 * The primary Category A signal is `field.isCategoryA` from the database.
 * This allowlist is a backstop: if a field has a known-sensitive fieldKey
 * but its `isCategoryA` flag was not set (backfill gap, admin oversight),
 * the input dispatcher treats it as Category A anyway.
 *
 * Adding a fieldKey here does NOT replace the need to tag it in the DB —
 * it prevents silent data leakage during the window between a new field
 * being added and its isCategoryA flag being explicitly set.
 */

/**
 * fieldKey values that are ALWAYS Category A regardless of the DB flag.
 * Derived from data-classification.md Category A taxonomy.
 *
 * Tagged by field type (password, pin) via migration v1.
 * Tagged by fieldKey via migration v2 (social_insurance_number, health_card_number,
 * security_qa, id_number).
 * Tagged by fieldKey via migration v3 / schema completeness (totp_seed, wallet_seed_phrase,
 * wallet_password added as real DB fields).
 * Remaining fieldKeys below are for fields not yet in the DB schema but known
 * from the data-classification spec — they act as a forward-compatible net.
 */
import { FIELD_KEYS } from '@shared/constants/fields';

export const CATEGORY_A_FIELD_KEYS = new Set([
  // Government IDs — seeded keys, use constants
  FIELD_KEYS.SOCIAL_INSURANCE_NUMBER,
  FIELD_KEYS.HEALTH_CARD_NUMBER,
  FIELD_KEYS.ID_NUMBER,
  // Credentials — seeded keys, use constants
  FIELD_KEYS.PASSWORD,
  FIELD_KEYS.PIN,
  FIELD_KEYS.SECURITY_QA,
  // Forward-compat net: keys below are not yet seeded as DB fields.
  // Remain as literals until added to shared/constants/fields.ts.
  "security_question",
  "security_answer",
  "totp_seed",
  "wallet_seed_phrase",
  "wallet_password",
  "passport_number",
  "drivers_licence_number",
  "two_fa_seed",
  "mfa_seed",
  "wallet_seed",
  "seed_phrase",
  "recovery_phrase",
  "crypto_seed_phrase",
  "entry_alarm_code",
]);

/**
 * Returns true if a field should be treated as Category A.
 * Checks BOTH the DB flag and the allowlist — fail-closed.
 */
export function isCategoryAField(field: {
  isCategoryA?: boolean;
  fieldKey?: string;
  fieldType?: string;
} | null | undefined): boolean {
  if (!field) return false;
  if (field.isCategoryA) return true;
  if (field.fieldKey && CATEGORY_A_FIELD_KEYS.has(field.fieldKey)) return true;
  // Belt-and-suspenders: password and pin field types are always Category A
  if (field.fieldType === "password" || field.fieldType === "pin") return true;
  return false;
}


